<script setup>
import { computed, ref, watch } from 'vue'
import { DAY_NAMES, FREQUENCY_NAMES } from '@/models/utils/Cron.js'
import DatePicker from '@/components/Forms/DatePicker.vue'
import { DatePickerObj } from '@/models/utils/InputUtils.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import InputText from '@/components/Forms/InputText.vue'
import ListBox from '@/components/Forms/ListBox.vue'
import ToggleSwitch from '@/components/Forms/ToggleSwitch.vue'
import { Util } from '@/models/utils/Util.js'
import { scheduledReportsApi } from '@/api/ScheduledReports/ScheduledReportsApi.js'
import { useAuthStore } from '@/stores/authStore.js'
import { Format } from '@/models/utils/FormatUtils.js'

const props = defineProps({
	open: { type: Boolean, default: false },
	report: { type: Object, default: null },
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
});

const emit = defineEmits(['evCloseModal']);
const isSaving = ref(false)
const authStore = useAuthStore()
const showDiscardChangesModal = ref(false);
const toaster = new Toaster();

const report_name = ref('')
const report_description = ref('')
const report_enabled = ref(false)
const validationErrors = ref(null)
const run_weekday = ref(null)
const run_frequency = ref(null)

/**
 * @type {import('vue').Ref<DatePickerObj>}
 */
const date_config = ref(new DatePickerObj('', {
		dateFormat: 'Y-m-d H:i',
		altFormat: 'h:i K',
		noCalendar: true,
		enableTime: true,
		time_24hr: false,
		minuteIncrement: 1,
	}))

/**
 *
 * @type {import('vue').Ref<{run_at: string, name: string, description: string, deleted_at: string, frequency: string, id: number}>}
 */
const init_data = ref({})
const run_description = computed(() => {
	if (!run_frequency.value) return ''

	let timeString = date_config.value.date ? ' at ' + new Date(date_config.value.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''

	switch (run_frequency.value.text) {
		case 'Daily':
			return `Report will run daily${timeString}.`
		case 'Weekly':
			return `Report will run weekly on ${run_weekday.value.text}s${timeString}.`
		case 'Monthly':
			return 'Report will run monthly at the end of each month.'
		case 'Quarterly':
			return 'Report will run quarterly at the end of each quarter.'
		case 'Yearly':
			return 'Report will run yearly at the end of each year.'
	}

	return ''
})

// Populate the form from the report's existing cron expression each time it opens.
watch(
	() => [props.open, props.report],
	() =>
	{
		if (!props.open || !props.report) return

		report_name.value = props.report.name
		report_description.value = props.report.description
		report_enabled.value = props.report.deleted_at == null
		run_frequency.value = getRunFrequency(props.report.frequency)

		if (props.report.run_at) {
			const runAt = new Date(props.report.run_at)

			date_config.value.date = Format.dateToStr(runAt, '-', 'YYYY-MM-DD HH:MM')
			run_weekday.value = {
				id: runAt.getDay(), // Starts at 0 - Sunday
				text: runAt.toLocaleDateString('en-US', { weekday: 'long' }),
			}
		}
		else {
			run_weekday.value = {
				id: 0, // Sunday
				text: DAY_NAMES[0],
			}
		}

		init_data.value = Util.deepCopy(props.report)
	},
	{ immediate: true }
)

/**
 * Submits changes to the report.
 */
function submit()
{
	isSaving.value = true

	const data = {
		id: props.report.id,
		source_report_id: props.report.source_report_id,
		name: report_name.value,
		description: report_description.value,
		enabled: report_enabled.value,
		frequency: run_frequency.value,
		run_weekday: run_weekday.value,
		run_time: new Date(date_config.value.date)
	}

	if (props.report.id) {
		scheduledReportsApi.updateReport(data, onSubmitComplete)
	}
	else {
		scheduledReportsApi.createReport(data, onSubmitComplete)
	}
}

/**
 * Called after submit completes
 * @param p_response
 * @param _p_type
 * @param p_error_eh
 */
function onSubmitComplete(p_response, _p_type, p_error_eh)
{
	isSaving.value = false

	try
	{
		if (p_error_eh)
		{
			validationErrors.value = p_response.showErrorBanner()
		}
		else
		{
			const data = p_response.getData();
			if (data && data.success)
			{
				if (props.report.id) {
					emit('evCloseModal', 'edit', true, { id: props.report.id });
				}
				else {
					emit('evCloseModal', 'duplicate', true, { id: props.report.id });
				}
			}
			else if (data && !data.success)
			{
				toaster.error(data.error_message)
			}
			else
			{
				toaster.error("Failed to update report. Please try again.")
			}
		}
	}
	catch (e)
	{
		toaster.error("An error occurred while updating the report.")
		if (authStore.isDeveloper) console.error(e)
	}
}

/**
 * Checks if any changes were made to determine if the discard modal should be shown when cancelling.
 * @type {import('vue').ComputedRef<boolean>}
 */
const changesMade = computed(() => {
	if (init_data.value.id === null) return false//If duplicating, isDuplicate will be checked to enable Save

	// Only check day/time for daily and weekly frequencies
	const checkDayTime = run_frequency.value?.id === 1 || run_frequency.value?.id === 2 // See FREQUENCY_NAMES in Cron.js

	const initialRunAt = init_data.value.run_at ? new Date(init_data.value.run_at) : null
	const currentRunAt = date_config.value.date ? new Date(date_config.value.date) : null

	const initialRunDay = init_data.value.run_at ? new Date(init_data.value.run_at).getDay() : null
	const currentRunDay = run_weekday.value ? run_weekday.value.id : null

	return (
		(init_data.value.name !== report_name.value) ||
		(init_data.value.description !== report_description.value) ||
		((init_data.value.deleted_at == null) !== report_enabled.value) ||
		getRunFrequency(init_data.value.frequency)?.id !== run_frequency.value?.id ||
		(checkDayTime &&
			(
				initialRunAt?.getHours() !== currentRunAt?.getHours() ||
				initialRunAt?.getMinutes() !== currentRunAt?.getMinutes() ||
				initialRunDay !== currentRunDay
			)
		)
	)
})

/**
 * If the id is null then we assume we're duplicating an existing report.
 * @type {import('vue').ComputedRef<boolean>}
 */
const isDuplicate = computed(() => {
	return init_data.value.id === null;
})

/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	if (!p_reload_eh && !props.modalError.error_eh && changesMade.value)
	{
		toggleDiscardChangesModal(true)
	}
	else
	{
		closeModal(p_reload_eh)
	}
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Do cleanup, show success toast, and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	init_data.value = {}
	toggleDiscardChangesModal(false)
	if (p_reload_eh) toaster.success("Your document has been successfully added.")
	emit('evCloseModal', 'edit', p_reload_eh)
}

function getRunFrequency(frequencyText) {
	return FREQUENCY_NAMES.find((frequency) => frequency.text.toLowerCase().replace('-', '_') === frequencyText.toLowerCase())
}

</script>

<template>
	<div>
		<BaseModal :show="open" border-classes="border-t-6 border-tertiary">
			<div class="w-136 h-192">
				<div class="flex justify-between border-b border-neutral w-full pb-4">
					<p class="flex-grow text-xl font-bold text-heading">{{ props.report.id !== null ? 'Edit' : 'New' }} Scheduled Report</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="props.modalError.error_eh">
					<div class="w-130 mt-6 text-error font-bold">{{ props.modalError.error_msg }}</div>
				</div>

				<form>
					<div class="flex flex-col mt-5">

						<InputText label="Report Name*" :required="false" type="text" placeholder="" input-height="h-12" autocomplete="off"
						           v-model="report_name" :errors="validationErrors" error-field="report_data.name" :disabled="false" >
							<template #toggle>
								<div class="flex flex-row" v-show="!isDuplicate">
									<span class="mr-2">Enabled</span>
									<ToggleSwitch screen_reader_text="Report Enabled" v-model="report_enabled" size="small" ></ToggleSwitch>
								</div>
							</template>
						</InputText>

						<InputText label="Description*" type="textarea" v-model="report_description" font-weight="font-normal" text-area-rows="2"
						           :errors="validationErrors" error-field="report_data.description"/>

						<ListBox
							 label="Frequency*"
							 v-model="run_frequency"
							 :items="FREQUENCY_NAMES"
							 :showListBoxOutline="true"
							 :showOptionsOutsideModal="true"/>

						<ListBox
							v-if="run_frequency?.text === 'Weekly'"
							label="Day of week*"
							v-model="run_weekday"
							:items="DAY_NAMES"
							:showListBoxOutline="true"
							:showOptionsOutsideModal="true"/>

						<DatePicker
							v-if="run_frequency?.text === 'Daily' || run_frequency?.text === 'Weekly'"
							v-model="date_config"
							:errors="validationErrors"
							error-field="report_data.time"
							placeholder="Select a time"
							label="Time*" />

						<div v-if="run_frequency?.text !== 'On-Demand'">
							{{ run_description }}
						</div>
					</div>

					<div class="flex w-full flex-col-reverse sm:flex-row mt-4">
						<div class="sm:mr-auto mt-1 sm:mt-0"></div>
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
						              class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
						<SubmitButton label="Save" :size="ButtonSize.Small" @click.prevent="submit" :disabled="(!isDuplicate && !changesMade) || isSaving" :loading="isSaving" />
					</div>
				</form>
			</div>
		</BaseModal>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="info"
				title="Discard Changes"
				message="Are you sure?"
				subMessage="Leaving now will discard any changes made."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardChangesModal(false) }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { closeModal(false) }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>
	</div>
</template>
